import { useState, useEffect } from "react";
import axios from "axios";
import { Star, Trophy, Medal, Award, Users, TrendingUp, MessageSquare } from "lucide-react";
import { StarRating, LeaveReviewModal } from "../components/ReputationBadge";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const USER_TYPE_COLORS = {
  investor: "#D4AF37",
  lender: "#0A84FF",
  contractor: "#FF9F0A",
  realtor: "#00FF88",
  title: "#AF52DE",
};

const CATEGORY_LABELS = {
  general: "General",
  communication: "Communication",
  reliability: "Reliability",
  expertise: "Expertise",
  deal_quality: "Deal Quality",
};

function RatingBar({ label, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-white/50 w-16 text-right">{label}</span>
      <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
        <div className="h-full bg-[#D4AF37] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-white/30 w-7">{count}</span>
    </div>
  );
}

function RankMedal({ rank }) {
  if (rank === 1) return <Trophy className="w-5 h-5 text-[#D4AF37]" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-white/50" />;
  if (rank === 3) return <Award className="w-5 h-5 text-[#CD7F32]" />;
  return <span className="text-white/30 text-sm font-mono w-5 text-center">{rank}</span>;
}

export default function ReputationPage() {
  const [myRep, setMyRep] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingMy, setLoadingMy] = useState(true);
  const [loadingLb, setLoadingLb] = useState(true);
  const [activeTab, setActiveTab] = useState("my");
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [me, setMe] = useState(null);
  const { mainClass } = useSidebar();

  const fetchData = async () => {
    try {
      const [myRes, lbRes, meRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reputation/my`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/reputation/leaderboard`),
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
      ]);
      setMyRep(myRes.data);
      setLeaderboard(lbRes.data.leaderboard || []);
      setMe(meRes.data);
    } catch (err) {
      console.error("Failed to load reputation data:", err?.response?.data?.detail || err.message);
    }
    setLoadingMy(false);
    setLoadingLb(false);
  };

  useEffect(() => { fetchData(); }, []);

  const tabs = [
    { id: "my", label: "My Reputation", icon: Star },
    { id: "leaderboard", label: "Leaderboard", icon: Trophy },
  ];

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Sidebar />
      <main className={mainClass}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Reputation</h1>
            <p className="text-white/40 text-sm mt-0.5">Your trust score and community standing on DealForge</p>
          </div>
          {me && (
            <button
              onClick={() => { setTargetUser({ id: "prompt", name: "" }); setShowReviewModal(true); }}
              className="self-start sm:self-auto px-4 py-2 bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-semibold text-sm rounded-lg flex items-center gap-2 transition-all"
              data-testid="leave-review-btn"
            >
              <Star className="w-4 h-4" /> Leave a Review
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="overflow-x-auto scrollbar-none -mx-1 px-1">
          <div className="flex gap-1 p-1 bg-white/[0.03] border border-white/[0.06] rounded-lg w-fit min-w-max">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === t.id ? "bg-[#D4AF37] text-black" : "text-white/50 hover:text-white"
                }`}
                data-testid={`rep-tab-${t.id}`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── My Reputation ── */}
        {activeTab === "my" && (
          <div className="space-y-6">
            {loadingMy ? (
              <div className="flex justify-center py-20">
                <div className="w-6 h-6 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
              </div>
            ) : myRep?.total_reviews === 0 ? (
              <div className="text-center py-20 bg-white/[0.01] border border-white/[0.04] rounded-2xl">
                <Star className="w-12 h-12 text-white/10 mx-auto mb-3" />
                <p className="text-white/40 text-sm font-medium">No reviews yet</p>
                <p className="text-white/20 text-xs mt-1">Complete deals with other members to receive reviews</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Score Card */}
                <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-6 flex flex-col items-center text-center">
                  <div className="text-6xl font-black text-[#D4AF37] mb-2" data-testid="my-avg-rating">
                    {myRep.average_rating}
                  </div>
                  <StarRating rating={myRep.average_rating} size="lg" showNumber={false} />
                  <p className="text-white/30 text-sm mt-3">
                    Based on <span className="text-white">{myRep.total_reviews}</span> review{myRep.total_reviews !== 1 ? "s" : ""}
                  </p>
                  {myRep.badges?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-4 justify-center">
                      {myRep.badges.map(b => (
                        <span key={b.id} className="text-[10px] px-2.5 py-1 rounded-full border font-semibold"
                          style={{ color: b.color, borderColor: `${b.color}40`, background: `${b.color}10` }}>
                          {b.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Rating Distribution */}
                <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <TrendingUp className="w-4 h-4 text-white/40" />
                    <h3 className="text-white font-semibold text-sm">Rating Breakdown</h3>
                  </div>
                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map(n => (
                      <RatingBar
                        key={n}
                        label={`${n} star${n !== 1 ? "s" : ""}`}
                        count={myRep.rating_distribution?.[n] || 0}
                        total={myRep.total_reviews}
                      />
                    ))}
                  </div>
                </div>

                {/* Recent Reviews */}
                <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <MessageSquare className="w-4 h-4 text-white/40" />
                    <h3 className="text-white font-semibold text-sm">Recent Reviews</h3>
                  </div>
                  <div className="space-y-4 max-h-64 overflow-y-auto pr-1">
                    {myRep.reviews?.map((r, i) => (
                      <div key={i} className="border-b border-white/[0.04] pb-3 last:border-0 last:pb-0">
                        <div className="flex items-center justify-between mb-1">
                          <StarRating rating={r.rating} size="sm" />
                          <span className="text-[10px] text-white/30">
                            {new Date(r.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {r.category && r.category !== "general" && (
                          <span className="text-[10px] text-white/30 bg-white/[0.04] px-2 py-0.5 rounded mb-1.5 inline-block">
                            {CATEGORY_LABELS[r.category] || r.category}
                          </span>
                        )}
                        {r.comment && (
                          <p className="text-white/50 text-xs leading-relaxed">{r.comment}</p>
                        )}
                        <p className="text-white/20 text-[10px] mt-1">— {r.reviewer_name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Leaderboard ── */}
        {activeTab === "leaderboard" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-white/30 text-xs px-2">
              <Users className="w-3.5 h-3.5" />
              <span>Top-rated DealForge members with 2+ verified reviews</span>
            </div>
            {loadingLb ? (
              <div className="flex justify-center py-20">
                <div className="w-6 h-6 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-20 bg-white/[0.01] border border-white/[0.04] rounded-2xl">
                <Trophy className="w-12 h-12 text-white/10 mx-auto mb-3" />
                <p className="text-white/40 text-sm">No entries yet</p>
                <p className="text-white/20 text-xs mt-1">Members with 2+ reviews will appear here</p>
              </div>
            ) : (
              leaderboard.map((entry, idx) => (
                <div
                  key={entry.user_id}
                  className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                    idx === 0
                      ? "bg-[#D4AF37]/[0.05] border-[#D4AF37]/20"
                      : "bg-white/[0.02] border-white/[0.05] hover:border-white/10"
                  }`}
                  data-testid={`leaderboard-entry-${entry.user_id}`}
                >
                  <div className="w-8 flex items-center justify-center flex-shrink-0">
                    <RankMedal rank={idx + 1} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-semibold text-sm truncate">{entry.name}</p>
                      <span
                        className="text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize"
                        style={{
                          color: USER_TYPE_COLORS[entry.user_type] || "#D4AF37",
                          background: `${USER_TYPE_COLORS[entry.user_type] || "#D4AF37"}15`,
                        }}
                      >
                        {entry.user_type}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <StarRating rating={entry.avg_rating} size="sm" showNumber />
                      <span className="text-[10px] text-white/30">{entry.total_reviews} review{entry.total_reviews !== 1 ? "s" : ""}</span>
                    </div>
                    {entry.badges?.length > 0 && (
                      <div className="flex gap-1.5 mt-1.5">
                        {entry.badges.map(b => (
                          <span key={b.id} className="text-[9px] px-1.5 py-0.5 rounded border font-semibold"
                            style={{ color: b.color, borderColor: `${b.color}40`, background: `${b.color}10` }}>
                            {b.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-2xl font-black" style={{ color: idx === 0 ? "#D4AF37" : "rgba(255,255,255,0.6)" }}>
                      {entry.avg_rating}
                    </div>
                    <div className="text-[10px] text-white/20">/ 5.0</div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Leave Review Modal */}
      {showReviewModal && (
        <LeaveReviewModal
          targetUserId={targetUser?.id || ""}
          targetName={targetUser?.name || "a member"}
          onClose={() => { setShowReviewModal(false); setTargetUser(null); }}
          onSubmit={fetchData}
        />
      )}
    </main>
    </div>
  );
}
